import { useMemo } from 'react'
import { MarketOption, useProjectMarkets } from './useProjectMarkets'

export interface HiddenMarketsInfo {
  /** True only once markets have loaded and at least one applicable market is hidden. */
  isHidden: boolean
  /** The applicable hidden markets, alphabetical by label. */
  hiddenMarkets: MarketOption[]
  hiddenCount: number
  /** Applicable market count: all project markets, minus where the parent is hidden. */
  totalMarkets: number
}

/** "🇩🇪 Germany" when a flag is known, otherwise just the label. */
export const formatMarketLine = (market: MarketOption): string =>
  market.flag ? `${market.flag} ${market.label}` : market.label

/**
 * Pure core of {@link useHiddenMarketsLabel} (extracted so it can be unit-tested
 * without React/Apollo). Always reports the *hidden* markets, never the visible.
 *
 * `parentHiddenInMarkets` narrows the applicable set for sub-items (a response
 * option / matrix row only applies where its question is visible), matching what
 * MarketVisibilityDialog does. Intersecting with the known markets drops any
 * stale/deleted survey ids, and an empty market list (e.g. still loading) yields
 * `isHidden: false` so we never flash "Hidden in 0".
 */
export const computeHiddenMarketsInfo = (
  allMarkets: MarketOption[],
  hiddenInMarkets: string[],
  parentHiddenInMarkets: string[] = []
): HiddenMarketsInfo => {
  const parentHidden = new Set(parentHiddenInMarkets)
  const hidden = new Set(hiddenInMarkets)

  const applicable = allMarkets.filter(
    (market) => !parentHidden.has(market.surveyId)
  )
  const hiddenMarkets = applicable
    .filter((market) => hidden.has(market.surveyId))
    .sort((a, b) => a.label.localeCompare(b.label))

  const totalMarkets = applicable.length
  const hiddenCount = hiddenMarkets.length

  return {
    isHidden: totalMarkets > 0 && hiddenCount > 0,
    hiddenMarkets,
    hiddenCount,
    totalMarkets
  }
}

/**
 * Resolves an item's `hiddenInMarkets` (survey ids) into the display data every
 * market-visibility indicator needs, reusing {@link useProjectMarkets}.
 */
export const useHiddenMarketsLabel = (
  hiddenInMarkets: string[],
  parentHiddenInMarkets: string[] = []
): HiddenMarketsInfo => {
  const allMarkets = useProjectMarkets()

  return useMemo(
    () =>
      computeHiddenMarketsInfo(
        allMarkets,
        hiddenInMarkets,
        parentHiddenInMarkets
      ),
    [allMarkets, hiddenInMarkets, parentHiddenInMarkets]
  )
}
